/**
 * Product Showcase — 4 variants (hero_cards, spec_compare, switcher, editorial_lineup)
 */
import { useState } from 'react';

import { Body, Btn, Eyebrow, H2, H3, SectionFrame, VisualPlaceholder } from './primitives';
import type { ProductShowcaseContent } from '../schemas/product_showcase';

interface PS {
  content: ProductShowcaseContent;
}

export function ProductShowcaseHeroCards({ content }: PS) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body style={{ maxWidth: 640 }}>{content.subheadline}</Body>}
        </div>
        <div className="ls-grid ls-grid-3">
          {content.products.map((p, i) => (
            <div
              key={i}
              className="ls-card"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                padding: 0,
                overflow: 'hidden',
              }}
            >
              <VisualPlaceholder src={p.image} aspectRatio="1 / 1" label={p.name} />
              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {p.category && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--ls-text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                    }}
                  >
                    {p.category}
                  </div>
                )}
                <H3>{p.name}</H3>
                <Body>{p.tagline}</Body>
                {p.price && <div style={{ marginTop: 8, fontWeight: 600 }}>{p.price}</div>}
                {p.ctaLabel && (
                  <Btn variant="secondary" href={p.ctaHref ?? '#'}>
                    {p.ctaLabel}
                  </Btn>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SectionFrame>
  );
}

export function ProductShowcaseSpecCompare({ content }: PS) {
  // 全プロダクトの spec.label を集約 (空ラベルは除外)
  const allLabels = Array.from(
    new Set(
      content.products
        .flatMap((p) => (Array.isArray(p.specs) ? p.specs : []))
        .filter((s) => s.label)
        .map((s) => s.label),
    ),
  );
  return (
    <SectionFrame background="surface">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 48 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              minWidth: 600,
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    textAlign: 'left',
                    padding: 16,
                    color: 'var(--ls-text-muted)',
                    fontWeight: 500,
                  }}
                />
                {content.products.map((p, i) => (
                  <th
                    key={i}
                    style={{
                      textAlign: 'left',
                      padding: 16,
                      borderBottom: '1px solid var(--ls-border)',
                      fontFamily: 'var(--ls-font-heading)',
                      fontSize: '1.25rem',
                      fontWeight: 600,
                    }}
                  >
                    {p.name}
                    {p.price && (
                      <div
                        style={{
                          fontSize: '0.85rem',
                          color: 'var(--ls-text-muted)',
                          fontWeight: 400,
                          marginTop: 4,
                        }}
                      >
                        {p.price}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {allLabels.map((label, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: 16,
                      color: 'var(--ls-text-muted)',
                      borderBottom: '1px solid var(--ls-border)',
                    }}
                  >
                    {label}
                  </td>
                  {content.products.map((p, j) => {
                    const v =
                      (Array.isArray(p.specs) ? p.specs : []).find((s) => s.label === label)
                        ?.value ?? '—';
                    return (
                      <td
                        key={j}
                        style={{ padding: 16, borderBottom: '1px solid var(--ls-border)' }}
                      >
                        {v}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionFrame>
  );
}

export function ProductShowcaseSwitcher({ content }: PS) {
  const [active, setActive] = useState(0);
  const product = content.products[active];
  if (!product) return null;
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            borderBottom: '1px solid var(--ls-border)',
            flexWrap: 'wrap',
          }}
        >
          {content.products.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              style={{
                padding: '12px 20px',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: active === i ? 'var(--ls-accent)' : 'var(--ls-text-muted)',
                fontWeight: active === i ? 600 : 400,
                borderBottom: active === i ? '2px solid var(--ls-accent)' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {p.name}
            </button>
          ))}
        </div>
        <div className="ls-split-pair">
          <VisualPlaceholder src={product.image} aspectRatio="4 / 3" label={product.name} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <H3>{product.name}</H3>
            <Body>{product.tagline}</Body>
            {(() => {
              // ラベルまたは値が入っている spec のみ表示。すべて空なら ul ごと非表示。
              // specs に null 要素や非配列が混入していてもクラッシュしないよう防御。
              const specs = Array.isArray(product.specs) ? product.specs : [];
              const visible = specs.filter((s) => s && (s.label || s.value));
              if (visible.length === 0) return null;
              return (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                  }}
                >
                  {visible.map((s, i) => (
                    <li
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 16,
                        borderBottom: '1px solid var(--ls-border)',
                        padding: '8px 0',
                      }}
                    >
                      <span style={{ color: 'var(--ls-text-muted)', flexShrink: 0 }}>
                        {s.label}
                      </span>
                      <span style={{ fontWeight: 500, textAlign: 'right' }}>{s.value}</span>
                    </li>
                  ))}
                </ul>
              );
            })()}
            {product.ctaLabel && (
              <Btn variant="primary" href={product.ctaHref ?? '#'}>
                {product.ctaLabel}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </SectionFrame>
  );
}

export function ProductShowcaseEditorialLineup({ content }: PS) {
  return (
    <SectionFrame>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 64 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {content.eyebrow && <Eyebrow>{content.eyebrow}</Eyebrow>}
          <H2>{content.headline}</H2>
          {content.subheadline && <Body style={{ maxWidth: 640 }}>{content.subheadline}</Body>}
        </div>
        {content.products.map((p, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={i} className="ls-split-pair">
              <div className="ls-split-media" style={{ order: reverse ? 2 : 1 }}>
                <VisualPlaceholder src={p.image} aspectRatio="4 / 5" label={p.name} />
              </div>
              <div
                style={{
                  order: reverse ? 1 : 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                <H3 style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)' }}>{p.name}</H3>
                <Body style={{ fontSize: '1.0625rem' }}>{p.tagline}</Body>
                {p.price && <div style={{ fontWeight: 600, fontSize: '1.125rem' }}>{p.price}</div>}
                {p.ctaLabel && (
                  <Btn variant="primary" href={p.ctaHref ?? '#'}>
                    {p.ctaLabel}
                  </Btn>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </SectionFrame>
  );
}

export const productShowcaseVariants = {
  hero_cards: ProductShowcaseHeroCards,
  spec_compare: ProductShowcaseSpecCompare,
  switcher: ProductShowcaseSwitcher,
  editorial_lineup: ProductShowcaseEditorialLineup,
};
